import React, { useState } from "react";
import { Eye, Upload, X } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/lib/utils";
import { MAX_JD_FILE_UPLOAD_MB } from "@/config/constants";
import FilePreview from "@/components/shared/FilePreview";
import { Textarea } from "@/components/ui/Textarea";
import { useJDUpload } from "../hooks/useJDUpload";
import PreviewModal from "@/components/shared/PreviewModal";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { ButtonShadcn } from "@/components/ui/Button";

const Step1 = ({ data, setData, isProcessing, scrollRef }) => {
  const {
    fileInputRef,
    isDragging,
    selectedFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    triggerFileInput,
    handleFileSelect,
    resetUpload,
    setSelectedFile,
  } = useFileUpload(MAX_JD_FILE_UPLOAD_MB);

  const [previewOpen, setPreviewOpen] = useState(false);

  const { uploadJD: handleUpload, uploading: jdUploading, error,clearError  } = useJDUpload(setData);

  React.useEffect(() => {
    setData((prev) => ({
      ...prev,
      isUploading: jdUploading,
    }));
  }, [jdUploading]);

const handleCancel = () => {
    setSelectedFile(null);
    resetUpload();
    clearError();
    setData((prev) => ({
      ...prev,
      uploadError: false,
    }));
    if (fileInputRef?.current) {
      fileInputRef.current.value = "";
    }
  };
  React.useEffect(() => {
    setData((prev) => ({
      ...prev,
      file: selectedFile,
    }));
  }, [selectedFile]);

  React.useEffect(() => {
    if (data?.file && !selectedFile) {
      handleFileSelect(data.file);
    }
  }, [data.file]);

  const isUploading = jdUploading;
  const isProcessingLocal = isProcessing || isUploading;
  // Real bytes-sent progress from useJDUpload. At 100% the file is up and the
  // server is reading it, which is a different sentence to the user.
  const uploadProgress = data.uploadProgress ?? 0;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5 rounded-lg bg-background-secondary/60 px-3 py-3">
        <p className="text-sm font-medium text-text-primary">JD Mode <span className="text-red-400">*</span></p>
        <SegmentedControl
          ariaLabel="JD Mode"
          value={data.jdMode || "improve"}
          onChange={(value) => setData((prev) => ({ ...prev, jdMode: value || "improve" }))}
          options={[
            { label: "Improve", value: "improve" },
            { label: "Extract", value: "extract" },
          ]}
          className="w-full [&>button]:flex-1"
        />
        <p className="text-xs text-text-secondary">Improve rewrites your JD with AI. Extract keeps it exactly as written.</p>
      </div>

      {/* Title */}
      <div>
        <h3 className="text-sm md:text-md font-semibold text-text-primary">Upload or Paste Your Job Description</h3>
        <p className="text-xs md:text-sm text-text-primary mt-1 pb-3">Our AI will rewrite and improve it based on your preferences.</p>
      </div>

      {/* SHOW UPLOAD ONLY WHEN NO TEXT */}
      {!data.description?.trim() && (
        <>
          <div
            className={cn(
              "border-2 border-dashed border-border-primary rounded-xl bg-background-form/40 transition-colors cursor-pointer",
              isDragging && "border-blue-500",
            )}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={(e) => {
              if (isProcessingLocal) return;
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (!file) return;
              const accepted = handleFileSelect(file);
              if (accepted) handleUpload(file);
            }}
            onClick={!isProcessingLocal ? triggerFileInput : undefined}
          >
            <input
              disabled={isProcessingLocal}
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                const accepted = handleFileSelect(file);
                if (accepted) handleUpload(file);
                e.target.value = "";
              }}
            />

            <div className="flex flex-col items-center justify-center py-12 text-center">
              {jdUploading ? (
                <>
                  {/* <LoadingAnimation /> */}
                  <div className="loader my-3"></div>
                  <p className="text-sm text-text-primary">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : "Reading your file..."}</p>
                </>
              ) : (
                <>
                  <Upload size={40} className="text-text-primary mb-4 bg-background-icon/40 rounded-full p-[10px]" />
                  <p className="text-sm text-text-primary/80 mb-1">Drop your Job Description or click to browse</p>
                  <p className="text-xs text-text-blue">Click to browse</p>
                </>
              )}
            </div>
          </div>

          {error && <p className="text-red-400 text-xs text-center mt-2">{error}</p>}

          <div className="flex justify-between text-[10px] md:text-xs text-text-secondary w-full px-1 pb-4">
            <span>Supported: PDF and DOCX</span>
            <span>Maximum Size: {MAX_JD_FILE_UPLOAD_MB} MB</span>
          </div>
        </>
      )}

      {/* SHOW MESSAGE WHEN TEXT EXISTS */}
      {data.description?.trim() && !selectedFile && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs md:text-sm text-text-primary">You have added text. Clear it to upload a file.</p>
          <ButtonShadcn
            variant="outline"
            size="xs"
            onClick={() => setData((prev) => ({ ...prev, description: "" }))}
            className="flex-shrink-0 bg-background-tertiary"
          >
            Clear
          </ButtonShadcn>
        </div>
      )}

      {/* FILE PREVIEW */}
      {selectedFile && !isUploading && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-3 border border-border-primary bg-background-secondary">
          <FilePreview file={selectedFile} />
          <div className="flex-1 min-w-0">
            <p className="text-text-primary text-xs md:text-sm font-medium truncate">{selectedFile.name}</p>
            {/* This row only renders once the upload is finished (see the
                condition above), so it states the size and nothing about
                progress — the two spans here could never be reached. */}
            <p className="text-text-secondary text-[10px] md:text-xs mt-0.5">
              {selectedFile.size < 1024 * 1024 ? `${(selectedFile.size / 1024).toFixed(1)} KB` : `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
            </p>
          </div>
          <ButtonShadcn
            variant="ghost"
            size="icon-sm"
            disabled={isProcessingLocal}
            onClick={() => setPreviewOpen(true)}
            aria-label="Preview file"
            className="text-gray-400 hover:text-text-blue"
          >
            <Eye size={18} />
          </ButtonShadcn>
          <ButtonShadcn
            variant="ghost"
            size="icon-sm"
            disabled={isProcessingLocal}
            onClick={handleCancel}
            aria-label="Remove file"
            className="text-gray-400 hover:text-red-400"
          >
            <X size={18} />
          </ButtonShadcn>
          {previewOpen && (
            <PreviewModal
              file={{ filename: selectedFile?.name, originalFile: selectedFile }}
              isOpen={previewOpen}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </div>
      )}

      {/* TEXTAREA (always when no file) */}
      {!selectedFile && (
        <Textarea
          ref={scrollRef}
          placeholder="Paste your job description here..."
          value={data.description}
          onChange={(e) => setData((prev) => ({ ...prev, description: e.target.value }))}
          disabled={jdUploading || isProcessingLocal}
          className="min-h-[180px] text-text-primary text-sm resize-none bg-background-form/40 border border-border-primary w-full rounded-md p-3"
        />
      )}
    </div>
  );
};

export default Step1;
